import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useCart } from '../context/CartContext';
import { authService } from '../lib/api/apiService';
import { cartAPI } from '../api/endpoints';
import { normalizeProductToCartItem } from '../utils/productHelpers';
import useCurrentUser from './useCurrentUser';
import { getCartQueryKey } from './useCartQuery';
import toast from 'react-hot-toast';

/** Extrait l'id de ligne panier depuis la réponse POST (formes API variables). */
function extractCartLineIdFromAddResponse(res) {
  const d = res?.data?.data ?? res?.data;
  const created =
    d?.item ??
    d?.cartItem ??
    (Array.isArray(d?.items) ? d.items[d.items.length - 1] : null) ??
    (typeof d === 'object' && d && 'productId' in d ? d : null);
  if (created && typeof created === 'object') {
    return created._id ?? created.id ?? null;
  }
  return null;
}

/**
 * Hook personnalisé pour gérer l'ajout de produits au panier
 * Normalise la logique d'extraction des données du produit (image, prix, etc.)
 * 
 * @returns {Function} handleAddToCart - Fonction à appeler pour ajouter un produit au panier
 * 
 * @example
 * const handleAddToCart = useAddToCart();
 * 
 * // Dans un composant, utilisation simple :
 * <button onClick={() => handleAddToCart(product, quantity, selectedImageIndex, variantId, optionKey)}>
 *   Ajouter au panier
 * </button>
 */
const useAddToCart = () => {
  const { addItem } = useCart();
  const queryClient = useQueryClient();
  const { data: currentUser } = useCurrentUser({ forceRefresh: false });

  const handleAddToCart = useCallback((product, options = {}) => {
    if (!product) {
      console.error('Produit non défini');
      return;
    }

    const cartItem = normalizeProductToCartItem(product, options);


    if (!cartItem) {
      console.error('Impossible de normaliser le produit');
      return;
    }

    const isAuthenticated = authService.isAuthenticated();
    const userId = currentUser?._id || currentUser?.id;

    // Invité : panier local uniquement (persisté en localStorage)
    if (!isAuthenticated || !userId) {
      addItem(cartItem);
      return;
    }

    // Utilisateur connecté : API + panier local
    cartAPI.addCartItem(userId, {
      productId: cartItem.productId.toString(),
      quantity: cartItem.quantity,
    })
      .then((res) => {
        const lineId = extractCartLineIdFromAddResponse(res);
        addItem(lineId ? { ...cartItem, itemId: lineId } : cartItem);
        queryClient.invalidateQueries({ queryKey: getCartQueryKey(userId) });
      })
      .catch((error) => {
        console.error('❌ Erreur lors de l\'ajout au panier:', error);
        toast.error('Erreur lors de l\'ajout au panier. Veuillez réessayer.');
      });
  }, [addItem, queryClient, currentUser]);

  return handleAddToCart;
};

export default useAddToCart;

